import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Menu,
  MenuItem,
  useTheme,
  alpha,
  Stack,
  Chip,
  IconButton,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Timeline as TimelineIcon,
  Insights as InsightsIcon,
  Assessment as AssessmentIcon,
  FileDownload as DownloadIcon,
  FilterList as FilterIcon,
  FitnessCenter as FitnessIcon,
  MonitorHeart as HeartIcon,
  Restaurant as NutritionIcon,
  DirectionsRun as MovementIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { AnimatedMetricCard } from './AnimatedMetricCard';
import { AnimatedChart, ChartDataPoint } from './AnimatedChart';
import { ProgressRing } from './ProgressRing';
import { MetricInsightCard } from './MetricInsightCard';
import { SkeletonLoader } from './SkeletonLoader';
import { AssessmentTimeline } from './AssessmentTimeline';
import { toDate } from '../../../../shared/utils/dateHelpers';
import { format } from 'date-fns';

interface MetricCategory {
  name: string;
  metrics: Array<{
    name: string;
    category: string;
    initialValue: number | null;
    reviewValue: number | null;
    allReviewValues?: Array<{ value: number; date: Date }>;
    isPositive: boolean;
  }>;
}

interface EnhancedAssessmentMetricsProps {
  assessmentData: MetricCategory[];
  isLoading?: boolean;
  clientId: string;
  clientName: string;
  rawAssessmentData?: any[]; // Raw report submissions with timestamps
}

export const EnhancedAssessmentMetrics: React.FC<EnhancedAssessmentMetricsProps> = ({
  assessmentData,
  isLoading = false,
  clientId,
  clientName,
  rawAssessmentData = [],
}) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Process metrics for display
  const processedMetrics = useMemo(() => {
    const allMetrics = assessmentData.flatMap(category => 
      category.metrics.map(metric => ({
        ...metric,
        categoryName: category.name,
      }))
    ).filter(metric => metric.initialValue !== null);

    return allMetrics;
  }, [assessmentData]);

  // Key metrics for hero display
  const heroMetrics = useMemo(() => {
    const priorityMetrics = ['Resting Heart Rate', 'VO2max', 'Skeletal Muscle Mass', 'Body Fat Percentage'];
    return processedMetrics.filter(metric => 
      priorityMetrics.includes(metric.name)
    ).slice(0, 4);
  }, [processedMetrics]);

  // Generate insights
  const insights = useMemo(() => {
    const generatedInsights = [];
    
    // Check for significant improvements
    processedMetrics.forEach(metric => {
      if (metric.reviewValue !== null && metric.initialValue !== null) {
        const percentChange = ((metric.reviewValue - metric.initialValue) / Math.abs(metric.initialValue)) * 100;
        const isImprovement = metric.isPositive ? metric.reviewValue > metric.initialValue : metric.reviewValue < metric.initialValue;
        
        if (Math.abs(percentChange) > 10) {
          if (isImprovement) {
            generatedInsights.push({
              id: `${metric.name}-improvement`,
              type: 'improvement' as const,
              title: `Great progress on ${metric.name}!`,
              message: `${clientName} has improved their ${metric.name} by ${Math.abs(percentChange).toFixed(1)}%. This is a significant achievement that shows dedication to the program.`,
              metric: metric.name,
              actionLabel: 'View Details',
              onAction: () => console.log('View details for', metric.name),
            });
          } else {
            generatedInsights.push({
              id: `${metric.name}-concern`,
              type: 'concern' as const,
              title: `${metric.name} needs attention`,
              message: `There's been a ${Math.abs(percentChange).toFixed(1)}% decline in ${metric.name}. Consider adjusting the training program or nutrition plan to address this.`,
              metric: metric.name,
              actionLabel: 'Create Action Plan',
              onAction: () => console.log('Create action plan for', metric.name),
            });
          }
        }
      }
    });

    // Add recommendations
    if (generatedInsights.length === 0) {
      generatedInsights.push({
        id: 'no-review-data',
        type: 'recommendation' as const,
        title: 'Schedule a review assessment',
        message: 'No review data is available yet. Schedule a review assessment to track progress and adjust the program as needed.',
        actionLabel: 'Schedule Assessment',
        onAction: () => console.log('Schedule assessment'),
      });
    }

    return generatedInsights;
  }, [processedMetrics, clientName]);

  const getMetricUnit = (metricName: string): string => {
    const units: Record<string, string> = {
      'Resting Heart Rate': 'bpm',
      'VO2max': 'ml/kg/min',
      'Skeletal Muscle Mass': 'kg',
      'Body Fat Percentage': '%',
      'Resting Metabolic Rate': 'kcal',
      'Weight': 'kg',
      'BMI': '',
    };
    return units[metricName] || '';
  };

  // Timeline events - Intelligent grouping and insights
  const timelineEvents = useMemo(() => {
    const events: any[] = [];

    // If no raw assessment data, try to use the processed metrics with their dates
    if (rawAssessmentData.length === 0 && processedMetrics.length === 0) {
      return events;
    }

    // Group metrics by assessment date
    const assessmentsByDate = new Map<string, any[]>();

    processedMetrics.forEach(metric => {
      // For initial values, try to find timestamp from raw data or use a default
      if (metric.initialValue !== null) {
        // Look for initial assessment in raw data
        const initialAssessment = rawAssessmentData.find(a =>
          a.report_type === 'Initial'
        );

        const initialDate = initialAssessment?.timestamp
          ? (initialAssessment.timestamp.toDate ? initialAssessment.timestamp.toDate() : new Date(initialAssessment.timestamp))
          : new Date(); // Fallback to current date if no timestamp

        const dateKey = format(initialDate, 'yyyy-MM-dd');
        if (!assessmentsByDate.has(dateKey)) {
          assessmentsByDate.set(dateKey, []);
        }
        assessmentsByDate.get(dateKey)!.push({
          name: metric.name,
          value: metric.initialValue,
          unit: getMetricUnit(metric.name),
          category: metric.categoryName,
          isPositive: metric.isPositive,
          stage: 'Initial',
          change: null,
        });
      }

      // Add review assessments with actual dates from the data
      if (metric.allReviewValues && metric.allReviewValues.length > 0) {
        metric.allReviewValues.forEach((review) => {
          const dateKey = format(review.date, 'yyyy-MM-dd');
          const previousValue = metric.initialValue;
          const percentChange = previousValue ? ((review.value - previousValue) / Math.abs(previousValue)) * 100 : 0;
          const isImprovement = metric.isPositive ? review.value > previousValue! : review.value < previousValue!;

          if (!assessmentsByDate.has(dateKey)) {
            assessmentsByDate.set(dateKey, []);
          }
          assessmentsByDate.get(dateKey)!.push({
            name: metric.name,
            value: review.value,
            unit: getMetricUnit(metric.name),
            category: metric.categoryName,
            isPositive: metric.isPositive,
            stage: 'Review',
            change: percentChange,
            isImprovement,
          });
        });
      }
    });

    // Create grouped assessment events
    assessmentsByDate.forEach((metrics, dateKey) => {
      const improvements = metrics.filter(m => m.change !== null && m.isImprovement).length;
      const declines = metrics.filter(m => m.change !== null && !m.isImprovement && m.change !== 0).length;
      const stable = metrics.filter(m => m.change === null || m.change === 0).length;
      const stage = metrics[0]?.stage || 'Assessment';

      // Calculate significance (average absolute change)
      const significantChanges = metrics.filter(m => m.change !== null && Math.abs(m.change) > 15);
      const avgChange = metrics
        .filter(m => m.change !== null)
        .reduce((sum, m) => sum + Math.abs(m.change!), 0) / Math.max(metrics.filter(m => m.change).length, 1);

      // Determine event type based on significance
      let eventType: 'assessment' | 'milestone' | 'improvement' | 'report' = 'assessment';
      let description = `${metrics.length} metrics assessed`;

      if (significantChanges.length >= 3) {
        eventType = 'milestone';
        description = `Breakthrough session: ${significantChanges.length} major improvements`;
      } else if (improvements > declines && improvements >= 3) {
        eventType = 'improvement';
        description = `${improvements} metrics improved • ${declines} declined • ${stable} stable`;
      } else if (declines > improvements) {
        description = `${improvements} improved • ${declines} metrics declined • ${stable} stable`;
      } else {
        description = `${improvements} improved • ${declines} declined • ${stable} stable`;
      }

      events.push({
        id: `session-${dateKey}`,
        date: new Date(dateKey),
        title: `${stage} Assessment Session`,
        description,
        type: eventType,
        status: 'completed' as const,
        metrics, // Store for expansion
        significance: avgChange,
        topChanges: metrics
          .filter(m => m.change !== null)
          .sort((a, b) => Math.abs(b.change!) - Math.abs(a.change!))
          .slice(0, 3),
      });
    });

    // Detect milestones
    const sortedEvents = events.sort((a, b) => a.date.getTime() - b.date.getTime());

    // Check for improvement streaks
    let streakCount = 0;
    sortedEvents.forEach((event, index) => {
      const improvements = event.metrics.filter((m: any) => m.isImprovement).length;
      const total = event.metrics.filter((m: any) => m.change !== null).length;

      if (total > 0 && improvements / total > 0.6) {
        streakCount++;
        if (streakCount >= 3 && index === sortedEvents.length - 1) {
          events.push({
            id: 'streak-milestone',
            date: event.date,
            title: '🎉 Consistency Milestone',
            description: `${streakCount} consecutive assessment periods showing improvement`,
            type: 'milestone' as const,
            status: 'completed' as const,
          });
        }
      } else {
        streakCount = 0;
      }
    });

    // Sort by date descending (most recent first) and limit intelligently
    return events.sort((a, b) => b.date.getTime() - a.date.getTime()).slice(0, 15);
  }, [processedMetrics, rawAssessmentData]);

  const getMetricIcon = (metricName: string) => {
    if (metricName.includes('Heart')) return <HeartIcon />;
    if (metricName.includes('Muscle') || metricName.includes('Strength')) return <FitnessIcon />;
    if (metricName.includes('Metabolic') || metricName.includes('Nutrition')) return <NutritionIcon />;
    if (metricName.includes('Movement') || metricName.includes('VO2')) return <MovementIcon />;
    return <AssessmentIcon />;
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const getMetricMaxValue = (metricName: string): number => {
    const maxValues: Record<string, number> = {
      'Resting Heart Rate': 100,
      'VO2max': 80,
      'Skeletal Muscle Mass': 50,
      'Body Fat Percentage': 40,
      'Resting Metabolic Rate': 3000,
      'Weight': 150,
      'BMI': 40,
    };
    return maxValues[metricName] || 100;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <SkeletonLoader variant="chart" height={400} />
          </Grid>
          <Grid item xs={12} md={6}>
            <SkeletonLoader variant="metric-card" count={2} />
          </Grid>
          <Grid item xs={12} md={6}>
            <SkeletonLoader variant="insight" />
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with Filters */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
          Assessment Metrics
        </Typography>

        {/* Category Filter Chips - Always Visible */}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            label="All Categories"
            onClick={() => setSelectedCategory(null)}
            color={!selectedCategory ? 'primary' : 'default'}
            variant={!selectedCategory ? 'filled' : 'outlined'}
            sx={{ fontWeight: !selectedCategory ? 600 : 400 }}
          />
          {assessmentData.map(category => (
            <Chip
              key={category.name}
              label={category.name}
              onClick={() => setSelectedCategory(category.name)}
              color={selectedCategory === category.name ? 'primary' : 'default'}
              variant={selectedCategory === category.name ? 'filled' : 'outlined'}
              sx={{ fontWeight: selectedCategory === category.name ? 600 : 400 }}
            />
          ))}
        </Stack>
      </Box>

      {/* Simplified Tabs */}
      <Paper elevation={0} sx={{ mb: 3, borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: 56,
              textTransform: 'none',
              fontWeight: 500,
            },
          }}
        >
          <Tab icon={<DashboardIcon />} label="Overview" iconPosition="start" />
          <Tab icon={<TimelineIcon />} label="Timeline" iconPosition="start" />
          <Tab icon={<InsightsIcon />} label="Insights" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === 0 && (
          <motion.div
            key="overview"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
          >
            {/* Hero Metrics - Focus on top 3, mobile responsive */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {heroMetrics.slice(0, 3).map((metric, index) => (
                <Grid item xs={12} sm={6} md={4} key={metric.name}>
                  <AnimatedMetricCard
                    metric={{
                      name: metric.name,
                      unit: getMetricUnit(metric.name),
                      initialValue: metric.initialValue || 0,
                      currentValue: metric.reviewValue || metric.initialValue || 0,
                      isPositive: metric.isPositive,
                      icon: getMetricIcon(metric.name),
                      category: metric.categoryName,
                    }}
                    delay={index * 0.05}
                    variant="hero"
                  />
                </Grid>
              ))}
            </Grid>

            {/* Summary Statistics Card - Mobile friendly */}
            <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.08)}`, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
              <Typography variant="h6" fontWeight={600} gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                Assessment Summary
              </Typography>
              <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mt: 0.5 }}>
                <Grid item xs={6} sm={4}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      Total Metrics Tracked
                    </Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
                      {processedMetrics.length}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      Metrics Improved
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="success.main" sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
                      {processedMetrics.filter(m => {
                        if (m.reviewValue === null || m.initialValue === null) return false;
                        return m.isPositive ? m.reviewValue > m.initialValue : m.reviewValue < m.initialValue;
                      }).length}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      Average Improvement
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="primary.main" sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
                      {(() => {
                        const improvements = processedMetrics
                          .filter(m => m.reviewValue !== null && m.initialValue !== null)
                          .map(m => {
                            const change = ((m.reviewValue! - m.initialValue!) / Math.abs(m.initialValue!)) * 100;
                            return m.isPositive ? change : -change;
                          })
                          .filter(change => change > 0);

                        const avg = improvements.length > 0
                          ? improvements.reduce((a, b) => a + b, 0) / improvements.length
                          : 0;

                        return `${avg.toFixed(1)}%`;
                      })()}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* Detailed Charts - Mobile optimized */}
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
              Detailed Metrics
              {selectedCategory && (
                <Chip
                  label={selectedCategory}
                  size="small"
                  onDelete={() => setSelectedCategory(null)}
                  sx={{ ml: 2 }}
                />
              )}
            </Typography>
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              {processedMetrics
                .filter(metric => !selectedCategory || metric.categoryName === selectedCategory)
                .map((metric, index) => {
                  const chartData: ChartDataPoint[] = [
                    { name: 'Initial', value: metric.initialValue || 0 },
                  ];

                  if (metric.allReviewValues && metric.allReviewValues.length > 0) {
                    metric.allReviewValues.forEach((review, idx) => {
                      chartData.push({
                        name: `Review ${idx + 1}`,
                        value: review.value,
                      });
                    });
                  } else if (metric.reviewValue !== null) {
                    chartData.push({ name: 'Review', value: metric.reviewValue });
                  }

                  return (
                    <Grid item xs={12} lg={6} key={metric.name}>
                      <AnimatedChart
                        data={chartData}
                        title={metric.name}
                        subtitle={`Category: ${metric.categoryName}`}
                        type="bar"
                        height={200}
                        unit={getMetricUnit(metric.name)}
                        isPositive={metric.isPositive}
                        delay={Math.min(index * 0.02, 0.2)}
                      />
                    </Grid>
                  );
                })}
            </Grid>
          </motion.div>
        )}

        {activeTab === 1 && (
          <motion.div
            key="timeline"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
          >
            <AssessmentTimeline
              events={timelineEvents}
              variant="vertical"
              onEventClick={(event) => console.log('Event clicked:', event)}
            />
          </motion.div>
        )}

        {activeTab === 2 && (
          <motion.div
            key="insights"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <MetricInsightCard
                  insights={insights}
                  onDismiss={(id) => console.log('Dismissed insight:', id)}
                />
              </Grid>
            </Grid>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default EnhancedAssessmentMetrics;